import { useState, useRef, useCallback, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from "react";

export interface NodePosition {
  x: number;
  y: number;
}

export interface UseCanvasProps<T extends NodePosition> {
  canvasWidth: number;
  canvasHeight: number;
  nodeWidth: number;
  nodeHeight: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  onNodesChange?: (updater: (nodes: T[]) => T[]) => void;
  onSelectionChange?: (nodeId: string) => void;
  onDirty?: () => void;
  getId?: (node: T) => string;
}

export function useCanvas<T extends NodePosition>({
  canvasWidth,
  canvasHeight,
  nodeWidth,
  nodeHeight,
  startX,
  startY,
  startWidth,
  startHeight,
  onNodesChange,
  onSelectionChange,
  onDirty,
  getId = (node: any) => node.id || node.sub_agents_id,
}: UseCanvasProps<T>) {
  const [camera, setCamera] = useState({ x: 20, y: 20, zoom: 1 });
  const [canvasTool, setCanvasTool] = useState<"select" | "pan">("select");
  const canvasRef = useRef<HTMLDivElement>(null);

  const startNodeDrag = useCallback((event: ReactPointerEvent, node: T) => {
    if (canvasTool !== "select" || event.button !== 0) return;
    event.stopPropagation();
    
    const id = getId(node);
    if (onSelectionChange) {
      onSelectionChange(id);
    }
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const offsetX = (event.clientX - rect.left - camera.x) / camera.zoom - node.x;
    const offsetY = (event.clientY - rect.top - camera.y) / camera.zoom - node.y;
    let moved = false;
    
    const move = (pointer: PointerEvent) => {
      moved = true;
      const x = (pointer.clientX - rect.left - camera.x) / camera.zoom - offsetX;
      const y = (pointer.clientY - rect.top - camera.y) / camera.zoom - offsetY;
      
      if (onNodesChange) {
        onNodesChange((current) => current.map((item) => getId(item) === id ? { ...item, x, y } : item));
      }
    };
    
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (moved && onDirty) onDirty();
    };
    
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, canvasTool, onNodesChange, onSelectionChange, onDirty, getId]);

  const startPan = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || (canvasTool !== "pan" && event.button !== 1 && event.button !== 0)) return;
    const origin = { clientX: event.clientX, clientY: event.clientY, x: camera.x, y: camera.y };
    
    const move = (pointer: PointerEvent) => setCamera((current) => ({
      ...current,
      x: origin.x + pointer.clientX - origin.clientX,
      y: origin.y + pointer.clientY - origin.clientY,
    }));
    
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [camera, canvasTool]);

  const zoomAt = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const nextZoom = Math.max(0.45, Math.min(1.7, camera.zoom * (event.deltaY > 0 ? 0.9 : 1.1)));
    const pointX = event.clientX - rect.left;
    const pointY = event.clientY - rect.top;
    const worldX = (pointX - camera.x) / camera.zoom;
    const worldY = (pointY - camera.y) / camera.zoom;
    
    setCamera({ zoom: nextZoom, x: pointX - worldX * nextZoom, y: pointY - worldY * nextZoom });
  }, [camera]);

  const fitCanvas = useCallback((nodes: T[]) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !nodes.length) return;
    
    const minX = Math.min(startX, ...nodes.map((node) => node.x));
    const minY = Math.min(startY, ...nodes.map((node) => node.y));
    const maxX = Math.max(startX + startWidth, ...nodes.map((node) => node.x + nodeWidth));
    const maxY = Math.max(startY + startHeight, ...nodes.map((node) => node.y + nodeHeight));
    
    const zoom = Math.max(0.45, Math.min(1.2, Math.min((rect.width - 100) / (maxX - minX), (rect.height - 100) / (maxY - minY))));
    setCamera({ zoom, x: 50 - minX * zoom, y: 50 - minY * zoom });
  }, [startX, startY, startWidth, startHeight, nodeWidth, nodeHeight]);

  return {
    camera,
    setCamera,
    canvasTool,
    setCanvasTool,
    canvasRef,
    startNodeDrag,
    startPan,
    zoomAt,
    fitCanvas,
  };
}
